import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

const CleverPopupCallback: React.FC = () => {
  const location = useLocation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authentication...');
  const currentMainURLQuery = useSelector((state: RootState) => state.app.currentMainURLQuery);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(currentMainURLQuery);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        if (error) {
          // Handle OAuth error
          console.error('OAuth error received:', error);
          setStatus('error');
          setMessage('Authentication failed: ' + error);

          // Send error message to parent window
          if (window.opener) {
            console.log('Sending error message to parent window');
            window.opener.postMessage(
              {
                type: 'CLEVER_AUTH_ERROR',
                error: error,
              },
              window.location.origin,
            );
          }
          return;
        }

        if (!code) {
          console.error('No authorization code received');
          setStatus('error');
          setMessage('No authorization code received');

          if (window.opener) {
            console.log('Sending error message to parent window');
            window.opener.postMessage(
              {
                type: 'CLEVER_AUTH_ERROR',
                error: 'No authorization code received',
              },
              window.location.origin,
            );
          }
          return;
        }

        // Send success message to parent window with code and state
        if (window.opener) {
          console.log('Sending success message to parent window with code and state');
          window.opener.postMessage(
            {
              type: 'CLEVER_AUTH_SUCCESS',
              code: code,
              state: state,
            },
            window.location.origin,
          );

          setStatus('success');
          setMessage('Authentication successful! This window will close automatically.');

          // Close popup after a short delay
          setTimeout(() => {
            console.log('Attempting to close window...');
            try {
              window.close();
              console.log('Window.close() called successfully');
              // If window.close() doesn't work, show a message to the user
              setTimeout(() => {
                if (!window.closed) {
                  console.log('Window did not close automatically, showing manual close message');
                  setMessage('Authentication successful! You can now close this window manually.');
                } else {
                  console.log('Window closed successfully');
                }
              }, 1000);
            } catch (error) {
              console.warn('Could not close window automatically:', error);
              setMessage('Authentication successful! You can now close this window manually.');
            }
          }, 2000);
        } else {
          console.error('No window.opener found, cannot communicate with parent window');
          setStatus('error');
          setMessage('Unable to communicate with parent window');
        }
      } catch (error) {
        console.error('Error handling Clever callback:', error);
        setStatus('error');
        setMessage('Authentication failed: ' + (error as Error).message);

        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'CLEVER_AUTH_ERROR',
              error: (error as Error).message,
            },
            window.location.origin,
          );
        }
      }
    };

    handleCallback();
  }, [location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Authenticating with Clever</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="text-green-500 text-4xl mb-4">✓</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Success!</h2>
              <p className="text-gray-600">{message}</p>
              <button
                onClick={() => {
                  try {
                    window.close();
                  } catch (error) {
                    console.warn('Could not close window:', error);
                  }
                }}
                className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Close Window
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-red-500 text-4xl mb-4">✗</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Failed</h2>
              <p className="text-gray-600">{message}</p>
              <button
                onClick={() => window.close()}
                className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Close Window
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CleverPopupCallback;
